if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.addEventListener('click', this.onReferenceVideoButtonClick.bind(this));
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.closest('.product--reference-gallery')) {
          this.referenceNavigationButtons = {
            previous: [
              this.elements.viewer.querySelector('.reference-gallery-nav__button--prev'),
              this.elements.thumbnails.querySelector('.reference-gallery-thumbnail-nav__button--prev'),
            ].filter(Boolean),
            next: [
              this.elements.viewer.querySelector('.reference-gallery-nav__button--next'),
              this.elements.thumbnails.querySelector('.reference-gallery-thumbnail-nav__button--next'),
            ].filter(Boolean),
          };

          this.referenceNavigationButtons.previous.forEach((button) => {
            button.addEventListener('click', () => this.showAdjacentMedia(-1));
          });
          this.referenceNavigationButtons.next.forEach((button) => {
            button.addEventListener('click', () => this.showAdjacentMedia(1));
          });
          this.updateReferenceNavigation();
        }
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      // 保修页：点右侧播放按钮，主图和按钮里的视频一起播
      onReferenceVideoButtonClick(event) {
        const button = event.target.closest('.reference-gallery-video-button');
        if (!button || !this.contains(button)) return;

        event.preventDefault();
        const buttonVideo = button.querySelector('.reference-gallery-video-button__video');
        if (buttonVideo) this.playReferenceButtonVideo(button, buttonVideo);
        if (button.dataset.mediaId) this.setActiveMedia(button.dataset.mediaId, false);
      }

      // 按钮内 mp4 预览：进入页面后静音循环播（移动端同样需要）
      connectedCallback() {
        this.querySelectorAll('.reference-gallery-video-button__video').forEach((video) => {
          video.muted = true;
          video.playsInline = true;
          video.loop = true;
          const playPromise = video.play();
          if (playPromise) playPromise.catch(() => {});
        });
      }

      playReferenceButtonVideo(button, video) {
        video.muted = true;
        video.loop = false;
        video.playsInline = true;
        video.currentTime = 0;
        const playPromise = video.play();
        if (playPromise) playPromise.catch(() => {});
        button.setAttribute('aria-pressed', 'true');
      }

      showAdjacentMedia(direction) {
        const thumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
        if (!thumbnails.length) return;

        const activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
        let activeIndex = thumbnails.findIndex(
          (thumbnail) => thumbnail.dataset.target === activeMedia?.dataset.mediaId
        );

        if (activeIndex < 0) {
          activeIndex = direction > 0 ? -1 : 0;
        }

        const nextIndex = activeIndex + direction;
        if (nextIndex < 0 || nextIndex >= thumbnails.length) {
          this.updateReferenceNavigation();
          return;
        }

        this.setActiveMedia(thumbnails[nextIndex].dataset.target, false);
      }

      updateReferenceNavigation() {
        if (!this.referenceNavigationButtons) return;

        const thumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
        const activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
        const activeIndex = thumbnails.findIndex(
          (thumbnail) => thumbnail.dataset.target === activeMedia?.dataset.mediaId
        );
        const currentIndex = Math.max(activeIndex, 0);

        this.referenceNavigationButtons.previous.forEach((button) => {
          button.disabled = currentIndex === 0;
        });
        this.referenceNavigationButtons.next.forEach((button) => {
          button.disabled = currentIndex >= thumbnails.length - 1;
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        // 视频项初始多为 display:none，IntersectionObserver 不会去掉 offscreen，
        // 激活后仍会保持 opacity≈0，移动端表现为“点了播放按钮没反应”
        activeMedia?.classList?.remove('scroll-trigger--offscreen');
        activeMedia?.classList?.add('scroll-trigger--cancel');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.updateReferenceVideoButton(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        this.updateReferenceNavigation();
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      // 视频正在播时，高亮右侧播放按钮
      updateReferenceVideoButton(activeMedia) {
        const button = this.querySelector('.reference-gallery-video-button');
        if (!button) return;

        const isVideoActive = activeMedia?.classList.contains('product__media-item--video');
        button.setAttribute('aria-pressed', isVideoActive ? 'true' : 'false');
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      // 当前主图是视频时，触发 DeferredMedia 插入 iframe / video
      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);

        const iframe = activeItem.querySelector('.reference-gallery-video-iframe');
        if (iframe) {
          const playFrameVideo = () => {
            const video = iframe.contentDocument?.querySelector('video');
            if (!video) return;
            video.loop = false;
            video.muted = true;
            video.playsInline = true;
            video.currentTime = 0;
            const playPromise = video.play();
            if (playPromise) playPromise.catch(() => {});
          };

          if (iframe.contentDocument?.querySelector('video')) {
            playFrameVideo();
          } else {
            iframe.addEventListener('load', playFrameVideo, { once: true });
          }
          return;
        }

        const video = activeItem.querySelector('video');
        if (!video) return;
        video.muted = true;
        video.playsInline = true;
        const playPromise = video.play();
        if (playPromise) playPromise.catch(() => {});
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
